import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowRight, Search, MapPin, Sparkles } from "lucide-react";
import EventCard from "../components/EventCard";
import VenueCard from "../components/VenueCard";
import RegionExplorer from "../components/RegionExplorer";
import useSEO from "../lib/useSEO";

const categories = [
  { key: "marche", label: "Marchés", img: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=900&q=85" },
  { key: "salon", label: "Salons", img: "https://images.unsplash.com/photo-1772903191730-fa9bc478c1de?w=900&q=85" },
  { key: "foire", label: "Foires", img: "https://images.pexels.com/photos/6950031/pexels-photo-6950031.jpeg?auto=compress&w=900" },
  { key: "festival", label: "Festivals", img: "https://images.unsplash.com/photo-1775866914966-5bd2194c464f?w=900&q=85" },
];

export default function Home() {
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [city, setCity] = useState("");

  useSEO({
    title: null,
    description:
      "EventMarket Pro — marketplace française qui réunit organisateurs, exposants, lieux d'exception et prestataires événementiels. Trouvez un événement, réservez un stand, louez un lieu.",
  });

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data.slice(0, 6)));
    api.get("/venues").then((r) => setVenues(r.data.slice(0, 3)));
  }, []);

  return (
    <div className="bg-cream">
      {/* Hero — Editorial */}
      <section className="relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 lg:pt-32 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <p data-testid="hero-tag" className="text-label text-accent mb-8 flex items-center gap-3">
                <span className="h-px w-8 bg-accent" />
                Marketplace événementielle — depuis 2026
              </p>
              <h1 className="font-heading font-light text-ink leading-[0.92] tracking-editorial text-5xl sm:text-6xl lg:text-7xl xl:text-8xl">
                Les plus beaux<br />
                <span className="font-display italic text-accent">événements</span><br />
                de France.
              </h1>
              <p className="mt-10 text-lg lg:text-xl text-ink/65 leading-relaxed max-w-xl font-light">
                Marchés rares, salons d'exception, festivals, foires choisies.
                <br className="hidden sm:block" />
                Réservez un stand. Trouvez un lieu. Construisez votre saison.
              </p>

              {/* Refined search */}
              <div className="mt-12 inline-flex items-center bg-white border border-ink/15 max-w-xl w-full">
                <div className="flex items-center gap-3 flex-1 px-5 py-4">
                  <MapPin className="h-4 w-4 text-ink/40" />
                  <Input
                    data-testid="hero-search-input"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Paris, Bretagne, Riquewihr…"
                    className="border-0 bg-transparent focus-visible:ring-0 text-ink placeholder:text-ink/30 text-sm h-auto p-0 shadow-none"
                  />
                </div>
                <Link to={`/events${city ? `?city=${encodeURIComponent(city)}` : ""}`}>
                  <Button
                    data-testid="hero-search-cta"
                    className="bg-ink hover:bg-ink/85 text-cream rounded-none px-6 py-7 h-auto border-l border-ink/15"
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </Link>
              </div>

              <div className="mt-12 flex flex-wrap gap-x-10 gap-y-3 text-[12px] text-ink/55">
                <span className="flex items-center gap-2">
                  <span className="h-1 w-1 rounded-full bg-accent" /> Profils vérifiés
                </span>
                <span className="flex items-center gap-2">
                  <span className="h-1 w-1 rounded-full bg-accent" /> Commission 20% transparente
                </span>
                <span className="flex items-center gap-2">
                  <span className="h-1 w-1 rounded-full bg-accent" /> Paiements sécurisés
                </span>
              </div>
            </div>

            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=900&q=90"
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-cream px-6 py-5 border border-ink/10 max-w-[240px]">
                  <p className="text-label text-ink/40 mb-2">Cette semaine</p>
                  <p className="font-heading text-2xl text-ink tracking-editorial leading-tight">
                    +<span className="font-display italic">120</span> événements
                  </p>
                  <p className="text-xs text-ink/50 mt-1">à travers les 13 régions</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="hairline-divider" />
        </div>
      </section>

      {/* Categories — refined */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-14 gap-4">
          <div>
            <p className="text-label text-ink/40 mb-4 flex items-center gap-3">
              <span className="h-px w-8 bg-ink/20" /> Catégories
            </p>
            <h2 className="font-heading font-light tracking-editorial text-4xl sm:text-5xl text-ink leading-[0.95]">
              L'événement <span className="font-display italic text-accent">parfait</span><br />
              pour chaque saison.
            </h2>
          </div>
          <Link to="/events" className="text-[13px] font-medium text-ink hover:text-accent flex items-center gap-2 group">
            Tout voir <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          {categories.map((c) => (
            <Link
              key={c.key}
              to={`/events?category=${c.key}`}
              data-testid={`category-${c.key}`}
              className="group relative overflow-hidden aspect-[3/4]"
            >
              <img
                src={c.img}
                alt={c.label}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[800ms] ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="font-heading font-light italic text-cream text-3xl tracking-editorial">{c.label}</p>
                <p className="text-[11px] text-cream/60 mt-2 uppercase tracking-[0.2em] flex items-center gap-2 group-hover:gap-3 transition-all">
                  Explorer <ArrowRight className="h-3 w-3" />
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured events */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-14 gap-4">
          <div>
            <p className="text-label text-ink/40 mb-4 flex items-center gap-3">
              <span className="h-px w-8 bg-ink/20" /> À l'affiche
            </p>
            <h2 className="font-heading font-light tracking-editorial text-4xl sm:text-5xl text-ink leading-[0.95]">
              Prochaines <span className="font-display italic">éditions</span>
            </h2>
          </div>
          <Link to="/events" className="text-[13px] font-medium text-ink hover:text-accent flex items-center gap-2 group">
            Calendrier complet <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((e) => <EventCard key={e.id} event={e} />)}
        </div>
      </section>

      {/* Featured venues */}
      <section className="bg-cream-200/40 border-y border-ink/10 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-14 gap-4">
            <div>
              <p className="text-label text-ink/40 mb-4 flex items-center gap-3">
                <span className="h-px w-8 bg-ink/20" /> Lieux d'exception
              </p>
              <h2 className="font-heading font-light tracking-editorial text-4xl sm:text-5xl text-ink leading-[0.95]">
                Des <span className="font-display italic text-accent">adresses</span><br />
                soigneusement choisies.
              </h2>
            </div>
            <Link to="/venues" className="text-[13px] font-medium text-ink hover:text-accent flex items-center gap-2 group">
              Toutes les adresses <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {venues.map((v) => <VenueCard key={v.id} venue={v} />)}
          </div>
        </div>
      </section>

      {/* Region explorer */}
      <RegionExplorer />

      {/* CTA — Editorial split */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <p className="text-label text-accent mb-4 flex items-center gap-3">
              <span className="h-px w-8 bg-accent" /> Pour les organisateurs
            </p>
            <h2 className="font-heading font-light tracking-editorial text-4xl sm:text-6xl text-ink leading-[0.95] mb-8">
              Publiez votre événement.<br />
              <span className="font-display italic text-accent">Gratuitement.</span>
            </h2>
            <p className="text-lg text-ink/65 mb-10 leading-relaxed font-light max-w-xl">
              Créez, gérez, recevez des candidatures d'exposants vérifiés.
              Aucun frais d'inscription. Vous ne payez qu'une commission sur les stands réellement vendus.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register">
                <Button data-testid="cta-organize" size="lg" className="bg-ink hover:bg-ink/85 text-cream rounded-none px-7">
                  Devenir organisateur <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <Link to="/register">
                <Button data-testid="cta-exhibit" size="lg" variant="outline" className="rounded-none border-ink/20 hover:bg-ink hover:text-cream">
                  Je suis exposant
                </Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/6950031/pexels-photo-6950031.jpeg?auto=compress&w=900"
                alt="Organisateurs"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-ink text-cream px-7 py-6 max-w-[260px]">
              <p className="text-label text-accent mb-2">Commission plateforme</p>
              <p className="font-heading font-light text-5xl tracking-editorial">
                <span className="font-display italic">20</span>%
              </p>
              <p className="text-xs text-cream/60 mt-2 leading-relaxed">
                Uniquement sur stands & lieux réservés. Aucun frais caché.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
